import type { BinColor } from '../../data/schemas';

/** Total length of the bin → hopper dump animation. Slow enough for small children to follow. */
export const DUMP_DURATION_MS = 2200;

/** Progress at which the first falling piece leaves the bin (play the dump sound here). */
export const DUMP_FALL_START = 0.4;
/** Progress at which the rubbish has landed and the truck's pile grows. */
export const DUMP_LAND_AT = 0.72;

/** Max pieces in the truck's pile; the pile fills up as bins are emptied. */
export const MAX_PILE_PIECES = 12;
/** How many pieces fall out of one bin. */
export const FALLING_PIECES = 6;

/** Hinge of the truck's hopper lid (local truck coordinates, just behind the cab). */
export const LID_HINGE = { x: 0.4, y: 2.0 } as const;
/** Fully open lid angle (rad). ~92°: the lid stands up behind the cab. */
export const LID_MAX_OPEN = 1.6;

/** Where the dumped bin's pivot hovers, local to the truck. Mouth ends up over this x. */
export const HOPPER_LOCAL = { x: -0.7, z: 0 } as const;
export const HOVER_HEIGHT = 3.3;

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function smooth(edge0: number, edge1: number, v: number): number {
  const t = clamp01((v - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

export interface DumpPhase {
  /** 0..1: lid openness (0 = at rest, 1 = fully open). */
  lid: number;
  /** 0..1: how far the bin has travelled from its pad to the hover point. */
  move: number;
  /** 0..1: how far the bin is tipped over the hopper. */
  tilt: number;
}

/**
 * Pure timeline for one dump, `t` in 0..1:
 *  0.00–0.25  lid opens, bin lifts toward the truck
 *  0.25–0.40  bin tips
 *  0.40–0.65  rubbish falls into the hopper
 *  0.65–0.80  bin untips
 *  0.80–1.00  bin returns, lid closes
 */
export function dumpPhase(t: number): DumpPhase {
  const u = clamp01(t);
  if (u <= 0) return { lid: 0, move: 0, tilt: 0 };
  const lid = smooth(0, 0.2, u) * (1 - smooth(0.82, 0.97, u));
  const move = smooth(0, 0.25, u) * (1 - smooth(0.8, 1, u));
  const tilt = smooth(0.25, 0.4, u) * (1 - smooth(0.65, 0.8, u));
  return { lid, move, tilt };
}

/** Max tilt of the bin while pouring (rad). */
export const BIN_MAX_TILT = 1.85;

/** Extra arc height while the bin travels (peaks mid-flight). */
export function arcHeight(move: number): number {
  return Math.sin(Math.PI * clamp01(move)) * 0.9;
}

/**
 * Hopper hover point in world XZ, shifted back so the tipped bin's mouth
 * (≈0.63 toward the hopper) ends up over the opening.
 */
export function hopperHoverWorld(
  pose: { x: number; z: number; yaw: number },
  binXZ: [number, number]
): { x: number; z: number; dirX: number; dirZ: number } {
  const c = Math.cos(pose.yaw);
  const s = Math.sin(pose.yaw);
  // Same convention as TruckModel: group.rotation.y = -yaw
  const hx = pose.x + HOPPER_LOCAL.x * c - HOPPER_LOCAL.z * s;
  const hz = pose.z + HOPPER_LOCAL.x * s + HOPPER_LOCAL.z * c;
  const dx = hx - binXZ[0];
  const dz = hz - binXZ[1];
  const d = Math.hypot(dx, dz) || 1;
  const dirX = dx / d;
  const dirZ = dz / d;
  const MOUTH_OFFSET = 0.63;
  return { x: hx - dirX * MOUTH_OFFSET, z: hz - dirZ * MOUTH_OFFSET, dirX, dirZ };
}

export interface TrashPieceDef {
  color: string;
  shape: 'box' | 'sphere' | 'cone';
  size: number;
}

/** Colourful, harmless rubbish per bin colour (organic / paper+plastic / other). */
export const TRASH_PALETTE: Record<BinColor, TrashPieceDef[]> = {
  green: [
    { color: '#8B5A2B', shape: 'box', size: 0.34 },
    { color: '#57CC5B', shape: 'sphere', size: 0.24 },
    { color: '#E9C46A', shape: 'cone', size: 0.3 },
  ],
  blue: [
    { color: '#F4F1DE', shape: 'box', size: 0.34 },
    { color: '#2F81F7', shape: 'box', size: 0.26 },
    { color: '#FFD166', shape: 'cone', size: 0.3 },
  ],
  red: [
    { color: '#F85149', shape: 'box', size: 0.32 },
    { color: '#9CA3AF', shape: 'sphere', size: 0.26 },
    { color: '#FFB703', shape: 'cone', size: 0.3 },
  ],
};

export function trashPiece(color: BinColor, index: number): TrashPieceDef {
  const list = TRASH_PALETTE[color];
  return list[index % list.length];
}

/** Heap poking out of an open bin, bin-local coordinates (rim is at y≈1.28). */
export const BIN_TRASH_SLOTS: [number, number, number][] = [
  [-0.18, 1.5, 0.12],
  [0.2, 1.55, -0.1],
  [0.02, 1.78, 0.02],
  [-0.2, 1.42, -0.2],
  [0.22, 1.42, 0.22],
];

/** Pile slots inside the truck's hopper (truck-local). Filled in order. */
export const PILE_SLOTS: [number, number, number][] = [
  [-1.15, 2.12, 0.3],
  [-0.85, 2.12, -0.3],
  [-0.5, 2.12, 0.25],
  [-1.25, 2.14, -0.35],
  [-0.2, 2.12, -0.2],
  [-0.95, 2.34, 0.0],
  [-0.55, 2.32, -0.05],
  [-1.2, 2.3, 0.4],
  [-0.3, 2.3, 0.3],
  [-0.75, 2.52, 0.15],
  [-1.05, 2.5, -0.25],
  [-0.45, 2.5, -0.3],
];

/** How many pile pieces to show for a 0..1 load. */
export function pileCount(loadAmount: number): number {
  if (loadAmount <= 0) return 0;
  return Math.max(1, Math.min(MAX_PILE_PIECES, Math.ceil(loadAmount * MAX_PILE_PIECES)));
}

/**
 * Lid angle (rad). Empty truck: lid shut. Once there is rubbish the lid rests ajar
 * (and a bit wider as the pile grows) so the pile stays visible on top of the hopper.
 */
export function lidAngle(loadAmount: number, lidOpen: number): number {
  const load = clamp01(loadAmount);
  const rest = load > 0 ? 0.22 + load * 0.3 : 0.04;
  return rest + (LID_MAX_OPEN - rest) * clamp01(lidOpen);
}

/** Position of a falling piece (truck-local) for dump progress `t`, or null when not falling. */
export function fallingPiece(
  index: number,
  t: number
): { x: number; y: number; z: number; spin: number } | null {
  const start = DUMP_FALL_START + index * 0.025;
  const end = start + 0.2;
  if (t <= start || t >= end) return null;
  const f = (t - start) / (end - start);
  const spread = [
    [0.0, 0.0],
    [0.25, 0.18],
    [-0.25, -0.15],
    [0.12, -0.25],
    [-0.15, 0.25],
    [0.3, -0.05],
  ][index % 6];
  const y = HOVER_HEIGHT - 0.2 - f * f * 1.15;
  return {
    x: HOPPER_LOCAL.x + spread[0],
    y,
    z: HOPPER_LOCAL.z + spread[1],
    spin: f * 6 + index,
  };
}
